import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import { DataGrid } from '@mui/x-data-grid';
import api from '../api';
import InvoiceCreateDialog from '../components/invoices/InvoiceCreateDialog';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [editCustomer, setEditCustomer] = useState(null);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);

  // 請求書
  const [openInvoice, setOpenInvoice] = useState(false);
  const [invoiceCustomer, setInvoiceCustomer] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    contact_name: '',
    contact_email: '',
    contact_tel: '',
    postal_code: '',
    address: '',
    tel: '',
  });

  /* =========================
   * load
   * ========================= */
  const loadCustomers = async () => {
    const res = await api.get('/api/customers');
    setCustomers(
      (res.data || []).map((c) => ({
        ...c,
        id: c.id.toString(),
      }))
    );
  };

  const loadUsers = async () => {
    try {
      const [usersRes, meRes] = await Promise.allSettled([
        api.get('/api/users'),
        api.get('/api/users/me'),
      ]);

      if (usersRes.status === 'fulfilled') {
        setAllUsers(usersRes.value.data || []);
      }

      if (meRes.status === 'fulfilled') {
        setCurrentUser(meRes.value.data || null);
      } else if (
        usersRes.status === 'fulfilled' &&
        usersRes.value.data?.length
      ) {
        setCurrentUser(usersRes.value.data[0]);
      }
    } catch {
      // noop
    }
  };

  useEffect(() => {
    loadCustomers();
    loadUsers();
  }, []);

  /* =========================
   * edit
   * ========================= */
  useEffect(() => {
    if (editCustomer) {
      setForm({
        name: editCustomer.name || '',
        contact_name: editCustomer.contact_name || '',
        contact_email: editCustomer.contact_email || '',
        contact_tel: editCustomer.contact_tel || '',
        postal_code: editCustomer.postal_code || '',
        address: editCustomer.address || '',
        tel: editCustomer.tel || '',
      });
      setOpen(true);
    }
  }, [editCustomer]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* 郵便番号 → 住所 */
  const fetchAddressByPostalCode = async (postalCode) => {
    const zip = postalCode.replace(/[^0-9]/g, '');
    if (zip.length !== 7) return;

    try {
      const res = await fetch(
        `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zip}`
      );
      const data = await res.json();
      if (data.results?.length) {
        const r = data.results[0];
        setForm((prev) => ({
          ...prev,
          address: `${r.address1}${r.address2}${r.address3}`,
        }));
      }
    } catch { }
  };

  const handleSubmit = async () => {
    const payload = { ...form };

    try {
      if (editCustomer) {
        await api.put(`/api/customers/${editCustomer.id}`, payload);
      } else {
        // xlsx 行75: 重複候補があれば 409 が返る、ユーザー確認で forceCreate=true で再送
        try {
          await api.post('/api/customers', payload);
        } catch (e) {
          if (e.response?.status === 409 && e.response?.data?.duplicates) {
            const list = e.response.data.duplicates.map(d => `・${d.name}`).join('\n');
            const ok = window.confirm(
              `${e.response.data.message}:\n${list}\n\nそれでも新規登録しますか？`
            );
            if (!ok) return;
            await api.post('/api/customers', { ...payload, forceCreate: true });
          } else {
            throw e;
          }
        }
      }

      setOpen(false);
      setEditCustomer(null);
      setForm({
        name: '',
        contact_name: '',
        contact_email: '',
        contact_tel: '',
        postal_code: '',
        address: '',
        tel: '',
      });
      loadCustomers();
    } catch (e) {
      alert(`保存できませんでした: ${e.response?.data?.error || e.message}`);
    }
  };

  const openInvoiceDialogFor = (row) => {
    setInvoiceCustomer(row);
    setOpenInvoice(true);
  };

  /* =========================
   * columns
   * ========================= */
  const columns = [
    // ID 列は不要 (xlsx feedback: 横スクロール解消)
    { field: 'name', headerName: 'クライアント名', flex: 1, minWidth: 200 },

    {
      field: 'contact',
      headerName: '担当者',
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1.4 }}>
          <Typography variant="body2" fontWeight="bold">
            {params.row.contact_name || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.contact_email || ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {params.row.contact_tel || ''}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'addressBlock',
      headerName: '住所',
      width: 260,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1.4 }}>
          <Typography variant="caption" color="text.secondary">
            {params.row.postal_code ? `〒${params.row.postal_code}` : ''}
          </Typography>
          <Typography variant="body2">
            {params.row.address || ''}
          </Typography>
        </Box>
      ),
    },

    { field: 'tel', headerName: '電話番号', width: 140 },

    // xlsx 行31: 契約数を表示、クリックで契約管理へ
    {
      field: 'contractCount',
      headerName: '契約',
      width: 80,
      renderCell: (params) => {
        const c = Number(params.value || 0);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {c > 0 ? (
              <Typography variant="body2" color="success.main">
                {c} 件
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">なし</Typography>
            )}
          </Box>
        );
      },
    },

    // xlsx 行71: 最終更新日 + 編集者
    {
      field: 'updatedAt',
      headerName: '最終更新',
      width: 170,
      renderCell: (params) => {
        const at = params.row.updatedAt;
        const by = params.row.updatedByName;
        if (!at) return <Typography variant="caption" color="text.secondary">-</Typography>;
        const d = new Date(at);
        const ymd = `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
        return (
          <Box sx={{ lineHeight: 1.3 }}>
            <Typography variant="caption" display="block">{ymd}</Typography>
            {by && <Typography variant="caption" color="text.secondary">by {by}</Typography>}
          </Box>
        );
      },
    },

    {
      field: 'actions',
      headerName: '',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
          <Tooltip title="編集">
            <IconButton
              size="small"
              onClick={() => setEditCustomer(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="削除">
            <IconButton
              size="small"
              onClick={() => setDeleteCustomerId(params.row.id)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="請求書発行">
            <Button
              size="small"
              variant="contained"
              startIcon={<ReceiptLongIcon />}
              onClick={() => openInvoiceDialogFor(params.row)}
              sx={{ ml: 0.5 }}
            >
              請求書
            </Button>
          </Tooltip>
        </Box>
      ),
    },
  ];

  /* =========================
   * render
   * ========================= */
  return (
    <PageContainer>
      <PageHeader
        title="クライアント一覧"
        actions={
          <Button
            variant="contained"
            onClick={() => {
              setEditCustomer(null);
              setOpen(true);
            }}
          >
            新規追加
          </Button>
        }
      />

      <DataGrid
        rows={customers}
        columns={columns}
        autoHeight
        pageSizeOptions={[10, 50]}
        disableRowSelectionOnClick
        getRowHeight={() => 72}
      />

      {/* 追加 / 編集 */}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditCustomer(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          クライアントを{editCustomer ? '編集' : '追加'}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="クライアント名"
              name="name"
              value={form.name}
              onChange={handleChange}
              fullWidth
              required
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <TextField label="担当者名" name="contact_name" value={form.contact_name} onChange={handleChange} />
              <TextField label="担当者メール" name="contact_email" value={form.contact_email} onChange={handleChange} />
              <TextField label="担当者電話" name="contact_tel" value={form.contact_tel} onChange={handleChange} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 2 }}>
              <TextField
                label="郵便番号"
                name="postal_code"
                value={form.postal_code}
                onChange={handleChange}
                onBlur={(e) => fetchAddressByPostalCode(e.target.value)}
                inputProps={{ inputMode: 'numeric' }}
              />
              <TextField
                label="住所"
                name="address"
                value={form.address}
                onChange={handleChange}
              />
            </Box>

            <TextField
              label="電話番号"
              name="tel"
              value={form.tel}
              onChange={handleChange}
              fullWidth
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setEditCustomer(null);
            }}
          >キャンセル</Button>
          <Button variant="contained" onClick={handleSubmit}>登録</Button>
        </DialogActions>
      </Dialog>

      {/* 削除 */}
      <Dialog open={Boolean(deleteCustomerId)} onClose={() => setDeleteCustomerId(null)}>
        <DialogTitle>クライアント削除確認</DialogTitle>
        <DialogContent>このクライアントを削除しますか？</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCustomerId(null)}>キャンセル</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              try {
                await api.delete(`/api/customers/${deleteCustomerId}`);
                setDeleteCustomerId(null);
                loadCustomers();
              } catch (e) {
                alert(`削除できませんでした: ${e.response?.data?.error || e.message}`);
              }
            }}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 請求書 */}
      {openInvoice && (
        <InvoiceCreateDialog
          open={openInvoice}
          onClose={() => setOpenInvoice(false)}
          customer={invoiceCustomer}
          currentUser={currentUser}
          users={allUsers}
          onSubmit={() => setOpenInvoice(false)}
        />
      )}
    </PageContainer>
  );
}
